import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getLocalizedSchema } from './schema';
import { FormLayout, Heading, Paragraph } from '@expressable/syntax-ds';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormUi } from './formUi';
import { useCognitoUserContext } from '../AWSCognitoWrapper/context';
import { useBoolean } from 'react-use';
import { LoginFormData } from '.';

export const LoginForm = (): JSX.Element => {
  const cognitoCtx = useCognitoUserContext();
  if (cognitoCtx.isLoggedIn) {
    throw new Error(`<Login/> component shouldn't render if there's an active session`);
  }

  const { logIn: onLogIn } = cognitoCtx;

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useBoolean(false);

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
    disabled: isLoading,
  });

  const { handleSubmit, setError } = methods;

  const onSubmit = async ({ email, password }: LoginFormData) => {
    setIsLoading(true);

    try {
      await onLogIn(email, password);
    } catch {
      setError('root', {
        message: t('login.form.error'),
      });
    }

    setIsLoading(false);
  };

  return (
    <>
      <div className="text-center max-w-[600px] mx-auto">
        <Heading level="h1">{t('login.pageHeading')}</Heading>
        <Paragraph size="lg" variant="primary">
          {t('login.subTitle')}
        </Paragraph>
      </div>
      <FormProvider {...methods}>
        <FormLayout onSubmit={handleSubmit(onSubmit)} className="max-w-[327px] mx-auto">
          <FormUi isLoading={isLoading} />
        </FormLayout>
      </FormProvider>
    </>
  );
};
